import { useEffect, useMemo, useState } from 'react';
import { supabase } from '../../lib/supabase';
import type { InvoiceDetail } from '../../lib/invoices';

interface Props {
  isOpen: boolean;
  invoice: InvoiceDetail['invoice'];
  onClose: () => void;
  onSaved: () => void;
}

interface FormState {
  nameOnInvoice: string;
  tav: string;
  email: string;
  address: string;
  notes: string;
}

interface CascadeState {
  enabled: boolean;
  street: string;
  postalCode: string;
  city: string;
}

interface UpdatePatch {
  Naam_op_factuur_Override__c?: string | null;
  Factuuradres_Override__c?: string | null;
  Factuur_e_mail_Override__c?: string | null;
  T_a_v__c?: string;
  Opmerkingen_op_factuur__c?: string;
}

/**
 * Splits het multi-line factuuradres in straat / "postcode plaats" voor de
 * cascade-form. Volgt het formula-formaat: regel 1 = straat, regel 2 = "postcode  plaats"
 * (de SF-formula gebruikt twee spaties tussen postcode en plaats).
 */
function parseAddress(addr: string): { street: string; postalCode: string; city: string } {
  if (!addr) return { street: '', postalCode: '', city: '' };
  const lines = addr
    .split(/\r?\n|<br\s*\/?>(?:\s*)?/i)
    .map((s) => s.trim())
    .filter(Boolean);
  const street = lines[0] || '';
  const second = lines[1] || '';
  const match = second.match(/^(\d{4}\s?[A-Za-z]{2})\s+(.+)$/);
  if (match && match[1] && match[2]) {
    return { street, postalCode: match[1].trim(), city: match[2].trim() };
  }
  // Fallback: hele tweede regel in city zodat gebruiker handmatig corrigeert.
  return { street, postalCode: '', city: second };
}

export function EditInvoiceAddressDialog({ isOpen, invoice, onClose, onSaved }: Props) {
  const initialName = useMemo(() => {
    const override = invoice.address.nameOverride || '';
    return override || invoice.address.nameOnInvoice || '';
  }, [invoice.address.nameOverride, invoice.address.nameOnInvoice]);

  const initialAddress = useMemo(() => {
    const override = invoice.address.formattedOverride || '';
    const raw = override || invoice.address.formatted || '';
    // Account-formula gebruikt BR() → raw bevat letterlijk "<br>". Voor de
    // textarea normaliseren we naar \n zodat de gebruiker netjes ziet wat
    // er staat. Bij opslaan blijft het multiline in het Override-veld.
    return raw.replace(/<br\s*\/?>(?:\s*)?/gi, '\n');
  }, [invoice.address.formattedOverride, invoice.address.formatted]);

  const [form, setForm] = useState<FormState>({
    nameOnInvoice: initialName,
    tav: invoice.address.tav || '',
    email: invoice.address.emailOverride || '',
    address: initialAddress,
    notes: invoice.notes || '',
  });
  const [cascade, setCascade] = useState<CascadeState>({
    enabled: false,
    street: '',
    postalCode: '',
    city: '',
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!isOpen) return;
    setForm({
      nameOnInvoice: initialName,
      tav: invoice.address.tav || '',
      email: invoice.address.emailOverride || '',
      address: initialAddress,
      notes: invoice.notes || '',
    });
    setCascade({ enabled: false, street: '', postalCode: '', city: '' });
    setError(null);
  }, [isOpen, invoice, initialName, initialAddress]);

  // Wanneer cascade wordt aangezet: pre-fill 3 sub-velden uit huidig adres.
  useEffect(() => {
    if (cascade.enabled && !cascade.street && !cascade.postalCode && !cascade.city) {
      const parsed = parseAddress(form.address);
      setCascade((c) => ({ ...c, ...parsed }));
    }
  }, [cascade.enabled, cascade.street, cascade.postalCode, cascade.city, form.address]);

  if (!isOpen) return null;

  const patch: UpdatePatch = {};
  if (form.nameOnInvoice.trim() !== (invoice.address.nameOverride || '').trim()) {
    patch.Naam_op_factuur_Override__c = form.nameOnInvoice.trim() || null;
  }
  // Vergelijk de huidige form-address (\n-genormaliseerd) met de huidige
  // Override (idem genormaliseerd) zodat we geen lege patch versturen wanneer
  // de gebruiker alleen de BR-naar-\n-rewrite ziet.
  const currentOverrideNormalized = (invoice.address.formattedOverride || '')
    .replace(/<br\s*\/?>(?:\s*)?/gi, '\n')
    .trim();
  if (form.address.trim() !== currentOverrideNormalized) {
    patch.Factuuradres_Override__c = form.address.trim() || null;
  }
  if (form.tav.trim() !== (invoice.address.tav || '').trim()) {
    patch.T_a_v__c = form.tav.trim();
  }
  if (form.email.trim() !== (invoice.address.emailOverride || '').trim()) {
    patch.Factuur_e_mail_Override__c = form.email.trim() || null;
  }
  if (form.notes !== (invoice.notes || '')) {
    patch.Opmerkingen_op_factuur__c = form.notes;
  }
  const hasChanges = Object.keys(patch).length > 0 || cascade.enabled;
  const isPaid = invoice.status.paid === true;

  function resetName() {
    setForm((f) => ({ ...f, nameOnInvoice: '' }));
  }
  function resetAddress() {
    setForm((f) => ({ ...f, address: '' }));
  }
  function resetEmail() {
    setForm((f) => ({ ...f, email: '' }));
  }

  async function handleSave() {
    if (!hasChanges) {
      onClose();
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      const token = session?.access_token;
      if (!token) throw new Error('Niet ingelogd');

      const accountAddress =
        cascade.enabled && (cascade.street || cascade.postalCode || cascade.city)
          ? {
              street: cascade.street.trim() || undefined,
              postalCode: cascade.postalCode.trim() || undefined,
              city: cascade.city.trim() || undefined,
            }
          : undefined;

      // Apex weigert lege patches; als er alleen een cascade is dan moet er
      // op invoice-niveau ook iets veranderen — anders skippen we het Apex-
      // call en doen we alleen de cascade. Hier kiezen we voor: als er geen
      // invoice-patch is maar wel cascade, mappen we de adres-edit op
      // Factuuradres_Override__c zodat de factuur en klant gelijk blijven.
      const effectivePatch: UpdatePatch = { ...patch };
      const hasInvoicePatch = Object.keys(effectivePatch).length > 0;

      if (!hasInvoicePatch && cascade.enabled) {
        onClose();
        return;
      }

      const response = await fetch('/api/invoices/update', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          invoiceId: invoice.id,
          patch: effectivePatch,
          cascadeToAccount: cascade.enabled,
          accountAddress,
        }),
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({ error: 'Opslaan mislukt' }));
        throw new Error(err.error || 'Opslaan mislukt');
      }

      onSaved();
      onClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Opslaan mislukt');
    } finally {
      setBusy(false);
    }
  }

  return (
    <>
      <div
        className="fixed inset-0 bg-black/40 z-[60]"
        onClick={busy ? undefined : onClose}
      />
      <div className="fixed inset-0 z-[70] flex items-center justify-center p-4 pointer-events-none">
        <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg pointer-events-auto max-h-[90vh] overflow-y-auto">
          <div className="px-5 py-4 border-b border-slate-200">
            <h3 className="text-base font-semibold text-slate-900">Factuurgegevens bewerken</h3>
            <p className="text-xs text-slate-500 mt-0.5">
              Naam, e-mailadres, adres en opmerkingen op deze factuur. Wijzigingen
              raken alleen deze factuur, tenzij je de cascade-optie hieronder aanvinkt.
            </p>
          </div>

          {isPaid && (
            <div className="px-5 pt-3">
              <p className="text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-3 py-2">
                Deze factuur is al voldaan. Wijzigingen blijven mogelijk maar zijn
                ongebruikelijk — overweeg of een creditnota of klacht-flow gepaster is.
              </p>
            </div>
          )}

          <div className="px-5 py-4 space-y-3">
            <FieldWithReset
              label="Naam op factuur"
              value={form.nameOnInvoice}
              onChange={(v) => setForm({ ...form, nameOnInvoice: v })}
              onReset={resetName}
              showReset={!!invoice.address.nameOverride}
              disabled={busy}
              placeholder="Standaard: klant- of werkordernaam"
            />
            <Field
              label="T.a.v."
              value={form.tav}
              onChange={(v) => setForm({ ...form, tav: v })}
              disabled={busy}
            />
            <FieldWithReset
              label="Factuur-e-mailadres"
              value={form.email}
              onChange={(v) => setForm({ ...form, email: v })}
              onReset={resetEmail}
              showReset={!!invoice.address.emailOverride}
              disabled={busy}
              type="email"
              placeholder={
                invoice.email
                  ? `Standaard: ${invoice.email}`
                  : 'Standaard: contact- of account-e-mail'
              }
            />
            <FieldWithReset
              label="Factuuradres"
              value={form.address}
              onChange={(v) => setForm({ ...form, address: v })}
              onReset={resetAddress}
              showReset={!!invoice.address.formattedOverride}
              disabled={busy}
              multiline
              rows={4}
              placeholder={'Straat 1\n1234 AB Plaats'}
            />
            <Field
              label="Opmerkingen op factuur"
              value={form.notes}
              onChange={(v) => setForm({ ...form, notes: v })}
              disabled={busy}
              multiline
              rows={3}
            />

            <label className="flex items-start gap-2 pt-2 cursor-pointer">
              <input
                type="checkbox"
                checked={cascade.enabled}
                onChange={(e) => setCascade({ ...cascade, enabled: e.target.checked })}
                disabled={busy}
                className="mt-0.5"
              />
              <span className="text-xs text-slate-700">
                Ook permanent bij deze klant opslaan (cascadeert naar Account billing-adres).
              </span>
            </label>

            {cascade.enabled && (
              <div className="ml-6 space-y-2 border-l-2 border-indigo-100 pl-3">
                <p className="text-[11px] text-slate-500">
                  Geef gestructureerd het billing-adres voor de Account. Voorgevuld
                  vanuit het factuuradres — corrigeer indien nodig.
                </p>
                <Field
                  label="Straat + huisnummer"
                  value={cascade.street}
                  onChange={(v) => setCascade({ ...cascade, street: v })}
                  disabled={busy}
                />
                <div className="grid grid-cols-3 gap-2">
                  <Field
                    label="Postcode"
                    value={cascade.postalCode}
                    onChange={(v) => setCascade({ ...cascade, postalCode: v })}
                    disabled={busy}
                    className="col-span-1"
                  />
                  <Field
                    label="Plaats"
                    value={cascade.city}
                    onChange={(v) => setCascade({ ...cascade, city: v })}
                    disabled={busy}
                    className="col-span-2"
                  />
                </div>
              </div>
            )}

            {error && (
              <p className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2">
                {error}
              </p>
            )}
          </div>

          <div className="px-5 py-3 border-t border-slate-200 flex gap-2 justify-end">
            <button
              type="button"
              onClick={onClose}
              disabled={busy}
              className="px-3 py-1.5 text-sm font-medium text-slate-600 border border-slate-300 rounded-md hover:bg-slate-50 disabled:opacity-50"
            >
              Annuleren
            </button>
            <button
              type="button"
              onClick={handleSave}
              disabled={busy || !hasChanges}
              className="px-3 py-1.5 text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
            >
              {busy ? 'Bezig...' : 'Opslaan'}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}

interface FieldProps {
  label: string;
  value: string;
  onChange: (v: string) => void;
  type?: string;
  disabled?: boolean;
  className?: string;
  multiline?: boolean;
  rows?: number;
  placeholder?: string;
}

function Field({
  label,
  value,
  onChange,
  type = 'text',
  disabled,
  className,
  multiline,
  rows = 3,
  placeholder,
}: FieldProps) {
  return (
    <label className={`block text-xs font-medium text-slate-600 ${className ?? ''}`}>
      {label}
      {multiline ? (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          rows={rows}
          placeholder={placeholder}
          className="mt-1 w-full px-3 py-2 text-sm text-slate-900 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400 font-mono"
        />
      ) : (
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={disabled}
          placeholder={placeholder}
          className="mt-1 w-full px-3 py-2 text-sm text-slate-900 border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent disabled:bg-slate-50 disabled:text-slate-400"
        />
      )}
    </label>
  );
}

interface FieldWithResetProps extends FieldProps {
  onReset: () => void;
  showReset: boolean;
}

function FieldWithReset({ label, onReset, showReset, ...props }: FieldWithResetProps) {
  return (
    <div>
      <div className="flex items-center justify-between mb-1">
        <label className="block text-xs font-medium text-slate-600">{label}</label>
        {showReset && (
          <button
            type="button"
            onClick={onReset}
            disabled={props.disabled}
            className="text-[11px] text-indigo-600 hover:underline disabled:opacity-50"
          >
            Reset naar standaard
          </button>
        )}
      </div>
      <Field {...props} label="" />
    </div>
  );
}
